import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import {
  Building2,
  Package,
  CreditCard,
  Users,
  MapPin,
  Settings,
  ChevronDown,
  ChevronRight,
  Save,
  Loader2,
  RefreshCw,
  Plus,
  ToggleLeft,
  ToggleRight,
  IndianRupee,
  Sparkles,
  ShoppingCart,
  ShoppingBag,
  LayoutDashboard,
  AlertCircle,
  CheckCircle2,
  Home,
  TrendingUp,
  BarChart2
} from 'lucide-react';

// Icon mapping for modules
const MODULE_ICONS = {
  home: Home,
  catalogue: Package,
  sales: ShoppingCart,
  purchases: ShoppingBag,
  finance_billing: IndianRupee,
  team_operations: Users,
  field_tracking: MapPin,
  intelligence: TrendingUp,
  settings: Settings,
  reports: BarChart2
};

const MODULE_COLORS = {
  home: { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
  catalogue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  sales: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  purchases: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  finance_billing: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  team_operations: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  field_tracking: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  intelligence: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
  settings: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  reports: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' }
};

/**
 * IndustryMenuEditor - Edit the actual FMCG menu structure
 * Shows the REAL modules and submodules that appear in the Owner sidebar
 */
const IndustryMenuEditor = () => {

  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [menuConfig, setMenuConfig] = useState(null);
  const [menuConfigFallback, setMenuConfigFallback] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planEntitlements, setPlanEntitlements] = useState(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('menu'); // 'menu' | 'plans'
  const [expandedModules, setExpandedModules] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [localLimits, setLocalLimits] = useState({});

  // Fetch industries
  const fetchIndustries = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/plans-v2/industries');
      setIndustries(data.industries || []);
      if (!selectedIndustry && data.industries?.length > 0) {
        setSelectedIndustry(data.industries[0]);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load industries'));
    }
  }, [selectedIndustry]);

  // Fetch menu config for selected industry
  const fetchMenuConfig = useCallback(async () => {
    if (!selectedIndustry) return;

    try {
      const { data } = await api.get(`/admin/plans-v2/industries/${selectedIndustry.id}/menu-config`);
      setMenuConfig(data.menu_config);
      setMenuConfigFallback(data.is_fallback || false);
      // Expand all modules by default
      const expanded = {};
      (data.menu_config?.modules || []).forEach(m => {
        expanded[m.key] = true;
      });
      setExpandedModules(expanded);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load menu config'));
    }
  }, [selectedIndustry]);

  // Fetch plans for selected industry
  const fetchPlans = useCallback(async () => {
    if (!selectedIndustry) return;

    try {
      const { data } = await api.get('/admin/plans-v2/plans', {
        params: { industry_id: selectedIndustry.id }
      });
      setPlans(data.plans || []);
      if (!selectedPlan && data.plans?.length > 0) {
        setSelectedPlan(data.plans[0]);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load plans'));
    }
  }, [selectedIndustry, selectedPlan]);

  // Fetch plan entitlements
  const fetchPlanEntitlements = useCallback(async () => {
    if (!selectedPlan) return;

    try {
      const { data } = await api.get(`/admin/plans-v2/plans/${selectedPlan.id}`);
      setPlanEntitlements(data.entitlements || {});
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load plan entitlements'));
    }
  }, [selectedPlan]);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchIndustries();
      setLoading(false);
    };
    load();
  }, [fetchIndustries]);

  // Load data when industry changes
  useEffect(() => {
    if (selectedIndustry) {
      fetchMenuConfig();
      fetchPlans();
    }
  }, [selectedIndustry, fetchMenuConfig, fetchPlans]);

  // Load plan entitlements when plan changes
  useEffect(() => {
    if (selectedPlan) {
      fetchPlanEntitlements();
    }
  }, [selectedPlan, fetchPlanEntitlements]);

  // Keep localLimits in sync with planEntitlements (used for limit inputs to avoid per-keystroke API calls)
  useEffect(() => {
    if (planEntitlements?.limits) {
      setLocalLimits(planEntitlements.limits);
    }
  }, [planEntitlements]);

  // Toggle module expansion
  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  // Toggle module enabled in plan
  const toggleModuleInPlan = async (moduleKey, enabled) => {
    if (!selectedPlan || !planEntitlements) return;
    
    try {
      setSaving(true);
      
      // Update local state immediately for responsiveness
      const newModules = { ...(planEntitlements.modules || {}), [moduleKey]: enabled };
      setPlanEntitlements(prev => ({ ...prev, modules: newModules }));
      
      // Save to backend
      await api.put(`/admin/plans-v2/plans/${selectedPlan.id}/entitlements`, {
        plan_id: selectedPlan.id,
        modules: newModules,
        submodules: planEntitlements.submodules || {},
        limits: planEntitlements.limits || {}
      });
      toast.success(`${moduleKey} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update module'));
      await fetchPlanEntitlements();
    } finally {
      setSaving(false);
    }
  };

  // Toggle submodule enabled in plan
  const toggleSubmoduleInPlan = async (submoduleKey, enabled) => {
    if (!selectedPlan || !planEntitlements) return;
    
    try {
      setSaving(true);
      
      // Update local state immediately
      const newSubmodules = { ...(planEntitlements.submodules || {}), [submoduleKey]: enabled };
      setPlanEntitlements(prev => ({ ...prev, submodules: newSubmodules }));
      
      // Save to backend
      await api.put(`/admin/plans-v2/plans/${selectedPlan.id}/entitlements`, {
        plan_id: selectedPlan.id,
        modules: planEntitlements.modules || {},
        submodules: newSubmodules,
        limits: planEntitlements.limits || {}
      });
      toast.success(`${submoduleKey} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update submodule'));
      await fetchPlanEntitlements();
    } finally {
      setSaving(false);
    }
  };

  // Update limit in plan
  const updateLimitInPlan = async (limitKey, value) => {
    if (!selectedPlan || !planEntitlements) return;
    
    try {
      setSaving(true);
      
      const newLimits = { ...(planEntitlements.limits || {}), [limitKey]: value };
      setPlanEntitlements(prev => ({ ...prev, limits: newLimits }));
      
      await api.put(`/admin/plans-v2/plans/${selectedPlan.id}/entitlements`, {
        plan_id: selectedPlan.id,
        modules: planEntitlements.modules || {},
        submodules: planEntitlements.submodules || {},
        limits: newLimits
      });
      toast.success(`Limit updated`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update limit'));
      await fetchPlanEntitlements();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col bg-white" data-testid="industry-menu-editor">
        <div className="flex-1 flex items-center justify-center bg-[#F8F9FB]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-rose-200 border-t-rose-500 rounded-full animate-spin" />
            <p className="text-slate-500 text-sm font-light">Loading menu configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="industry-menu-editor">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-[18px] font-semibold text-slate-800">Industry Menu Editor</h1>
              <p className="text-[12px] text-slate-500 mt-0.5">Configure real menu structure for Owner sidebar</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Industry Selector */}
              <div className="relative">
                <select
                  value={selectedIndustry?.id || ''}
                  onChange={(e) => {
                    const ind = industries.find(i => i.id === e.target.value);
                    setSelectedIndustry(ind);
                    setSelectedPlan(null);
                    setPlanEntitlements(null);
                  }}
                  className="pl-10 pr-8 py-2 border border-slate-200 rounded-lg text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-400 bg-white appearance-none min-w-[200px]"
                >
                  {industries.map(ind => (
                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                  ))}
                </select>
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              </div>
              
              <button
                onClick={() => {
                  fetchMenuConfig();
                  fetchPlans();
                  fetchPlanEntitlements();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Plan Selector Bar */}
      <div className="flex-shrink-0 bg-[#F8F9FB] px-6 py-3 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-medium text-slate-500">Configure for Plan:</span>
          <div className="flex items-center gap-2">
            {plans.map(plan => (
              <button
                key={plan.id}
                onClick={() => setSelectedPlan(plan)}
                className={`px-4 py-2 rounded-lg text-[13px] font-medium transition-all ${
                  selectedPlan?.id === plan.id
                    ? 'bg-rose-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-rose-300'
                }`}
              >
                {plan.name}
              </button>
            ))}
          </div>
          {saving && (
            <div className="flex items-center gap-2 ml-auto text-[12px] text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Menu Structure Panel */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-slate-100">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-[15px] font-semibold text-slate-800">
                      {selectedIndustry?.name} Menu Structure
                    </h3>
                    <p className="text-[12px] text-slate-500 mt-0.5">
                      These are the REAL modules shown in Owner sidebar
                    </p>
                  {menuConfigFallback && (
                    <p className="text-[11px] text-amber-600 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 flex-shrink-0" />
                      No template for this industry yet — showing General baseline. Any save will create a custom template.
                    </p>
                  )}
                  </div>
                  {selectedPlan && (
                    <span className="px-3 py-1 bg-rose-100 text-rose-700 text-[11px] font-medium rounded-full">
                      Editing: {selectedPlan.name}
                    </span>
                  )}
                </div>
              </div>
              
              <div className="divide-y divide-slate-100">
                {menuConfig?.modules?.map(module => {
                  const ModuleIcon = MODULE_ICONS[module.key] || Package;
                  const colors = MODULE_COLORS[module.key] || MODULE_COLORS.home;
                  const isExpanded = expandedModules[module.key];
                  const isModuleEnabled = planEntitlements?.modules?.[module.key] !== false;
                  const hasChildren = module.children && module.children.length > 0;
                  
                  return (
                    <div key={module.key} data-testid={`menu-module-${module.key}`}>
                      {/* Module Header */}
                      <div className={`flex items-center justify-between px-4 py-3 ${isModuleEnabled ? colors.bg : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-3 flex-1">
                          <button
                            onClick={() => hasChildren && toggleModule(module.key)}
                            className="flex items-center gap-2"
                          >
                            {hasChildren && (
                              isExpanded ? 
                                <ChevronDown className="w-4 h-4 text-slate-500" /> : 
                                <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                            <ModuleIcon className={`w-5 h-5 ${isModuleEnabled ? colors.text : 'text-slate-400'}`} />
                            <span className={`text-[14px] font-semibold ${isModuleEnabled ? 'text-slate-800' : 'text-slate-400'}`}>
                              {module.label}
                            </span>
                          </button>
                          {hasChildren && (
                            <span className="text-[11px] text-slate-500">
                              {module.children.length} items
                            </span>
                          )}
                        </div>
                        
                        {/* Module Toggle */}
                        <button
                          onClick={() => toggleModuleInPlan(module.key, !isModuleEnabled)}
                          disabled={saving || !selectedPlan}
                          className={`transition-colors ${!selectedPlan ? 'opacity-50 cursor-not-allowed' : ''}`}
                          title={selectedPlan ? `${isModuleEnabled ? 'Disable' : 'Enable'} ${module.label}` : 'Select a plan first'}
                        >
                          {isModuleEnabled ? (
                            <ToggleRight className="w-10 h-10 text-emerald-500" />
                          ) : (
                            <ToggleLeft className="w-10 h-10 text-slate-300" />
                          )}
                        </button>
                      </div>
                      
                      {/* Submodules */}
                      {hasChildren && isExpanded && (
                        <div className="bg-white">
                          {module.children.map(child => {
                            const isSubmoduleEnabled = planEntitlements?.submodules?.[child.key] !== false;
                            
                            return (
                              <div
                                key={child.key}
                                className={`flex items-center justify-between px-4 py-2.5 pl-12 border-t border-slate-50 ${
                                  !isModuleEnabled ? 'opacity-50' : ''
                                }`}
                                data-testid={`menu-submodule-${child.key}`}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`w-1.5 h-1.5 rounded-full ${isSubmoduleEnabled && isModuleEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                  <span className={`text-[13px] ${isSubmoduleEnabled && isModuleEnabled ? 'text-slate-700' : 'text-slate-400'}`}>
                                    {child.label}
                                  </span>
                                  <span className="text-[10px] text-slate-400 font-mono">
                                    {child.route}
                                  </span>
                                </div>
                                
                                <button
                                  onClick={() => toggleSubmoduleInPlan(child.key, !isSubmoduleEnabled)}
                                  disabled={saving || !selectedPlan || !isModuleEnabled}
                                  className={`transition-colors ${(!selectedPlan || !isModuleEnabled) ? 'opacity-30 cursor-not-allowed' : ''}`}
                                >
                                  {isSubmoduleEnabled ? (
                                    <ToggleRight className="w-8 h-8 text-emerald-500" />
                                  ) : (
                                    <ToggleLeft className="w-8 h-8 text-slate-300" />
                                  )}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Panel - Limits & Info */}
          <div className="space-y-4">
            {/* Plan Info */}
            {selectedPlan && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-rose-500" />
                  {selectedPlan.name}
                </h3>
                <div className="space-y-2 text-[12px]">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Monthly Price</span>
                    <span className="font-medium text-slate-700">₹{selectedPlan.price_monthly}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Yearly Price</span>
                    <span className="font-medium text-slate-700">₹{selectedPlan.price_yearly}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Included Staff</span>
                    <span className="font-medium text-slate-700">{selectedPlan.included_staff}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Included Tracking</span>
                    <span className="font-medium text-slate-700">{selectedPlan.included_tracking_staff}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Limits Configuration */}
            {selectedPlan && planEntitlements && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
                <h3 className="text-[14px] font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Settings className="w-4 h-4 text-rose-500" />
                  Plan Limits
                </h3>
                <div className="space-y-3">
                  {[
                    { key: 'max_staff_total', label: 'Max Staff', icon: Users },
                    { key: 'max_tracking_staff', label: 'Max Tracking Staff', icon: MapPin },
                    { key: 'max_products', label: 'Max Products', icon: Package },
                    { key: 'max_customers', label: 'Max Customers', icon: Users }
                  ].map(limit => (
                    <div key={limit.key} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <limit.icon className="w-4 h-4 text-slate-400" />
                        <span className="text-[12px] text-slate-600">{limit.label}</span>
                      </div>
                      <input
                        type="number"
                        value={localLimits[limit.key] ?? ''}
                        onChange={(e) => setLocalLimits(prev => ({ ...prev, [limit.key]: e.target.value ? parseInt(e.target.value) : null }))}
                        onBlur={(e) => {
                          const newVal = e.target.value ? parseInt(e.target.value) : null;
                          if (newVal !== (planEntitlements?.limits?.[limit.key] ?? null)) {
                            updateLimitInPlan(limit.key, newVal);
                          }
                        }}
                        placeholder="∞"
                        className="w-20 px-2 py-1 border border-slate-200 rounded text-[12px] text-right focus:outline-none focus:ring-2 focus:ring-rose-200"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[13px] font-medium text-amber-800">How it works</p>
                  <p className="text-[11px] text-amber-700 mt-1">
                    Toggle modules ON/OFF to control what appears in the Owner sidebar.
                    Changes apply immediately to all businesses on this plan.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryMenuEditor;
